import * as atlas from 'azure-maps-control';
import { memo, useEffect, useState } from 'react';

import { useAzureMap } from '@chr/online-shared-ui-maps';
import { VisibilityBooking } from '@/common/apis/dtos';
import { useTheme } from '@mui/material';

import {
  addRoutesToMap,
  createMapLayers,
  createWaypoints,
  dedupWaypoints,
  filterInvalidWaypoints,
  getMapMarkers,
  getOrderRoutes,
  handleMarkerUpdates,
  handleRouteUpdates
} from './mapHelpers';

import { useTrackingMap } from './TrackingMapProvider';
import { Toaster } from './Toaster';
import { MapError } from './MapError';
import { MappableStop } from './MapTypes';
import { useTranslation } from '@chr/online-shared-ui-localizations';

export interface MapDataLayerProps {
  stops: MappableStop[];
  booking: VisibilityBooking;
}

export const OrderMarkersLayer = memo(({ stops, booking }: MapDataLayerProps) => {
  const translate = useTranslation();
  const { map } = useTrackingMap();
  const { isMapReady, setMapStyle, mapStyle } = useAzureMap();
  const { palette } = useTheme();

  const [isCameraFocusedOnRoutes, setIsCameraFocusedOnRoutes] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [mapError, setMapError] = useState<Error>();

  // Use a Set with compound key of longitude and latitude to help dedupe waypoints.
  const plottedPoints = new Set<string>();

  /**
   * 1. Convert stops to waypoints.
   * 2. Filter out invalid waypoints.
   * 3. Dedupe waypoints.
   */
  const allWaypoints = createWaypoints(stops);
  const validWaypoints = filterInvalidWaypoints(allWaypoints);
  const waypoints = dedupWaypoints(validWaypoints, plottedPoints);
  const markers = getMapMarkers(waypoints, translate);
  const routes = getOrderRoutes(stops, booking);

  // Always clear the map of markers before adding new ones.
  if (map && map.markers) {
    map.markers.clear();
    map.markers.add(markers);
  }

  if (map && map.events) {
    // Handle click events on the markers.
    map.events.add('click', markers, (e) => {
      const popup = e.target?.getOptions()?.popup;
      if (popup) {
        popup.isOpen() ? popup.close() : popup.open(map);
      }
    });
  }

  // if the calculated route has no errors, add the routes to the map and adjust the map camera
  useEffect(() => {
    addRoutesToMap(
      map,
      isMapReady,
      routes.data?.calculatedRoutes,
      waypoints,
      isCameraFocusedOnRoutes,
      routes.isError,
      routes.isLoading,
      setIsCameraFocusedOnRoutes,
      setShowErrorToast
    );
  }, [map, waypoints, routes, isCameraFocusedOnRoutes, isMapReady]);

  // Create the layers and hook into events once
  useEffect(() => {
    createMapLayers(
      map as atlas.Map,
      isMapReady,
      setMapStyle,
      palette,
      setMapError
    );
  }, [isMapReady, map, mapStyle, setMapStyle]);

  // We need to update markers and route updates when mapStyle changes.
  useEffect(() => {
    if (map) {
      handleMarkerUpdates(map);
      handleRouteUpdates(map, mapStyle, palette);
    }

  }, [map, mapStyle, palette]);

  const dismissToast = (e: Event) => {
    e.preventDefault();
    setShowErrorToast(false);
  };

  return (
    <>
      {mapError && <MapError error={mapError} />}
      <Toaster
        message="The order route can't be displayed right now, but please check back later."
        details="You can still track your order in the Order Route details section below."
        showMessage={showErrorToast}
        dismissMessageHandler={dismissToast}
      />
    </>
  );
});
